import { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import Select from 'react-select';

import Header from './Header';
import SelectField from './SelectField';
import TextArea from './TextArea';
import TextField from './TextField';

import * as NCRDataConstants from '../constants/NCRDataConstants';

import GetScreenFieldValue from '../functions/GetScreenFieldValue';

import DataService from '../services/DataService';

import Task from '../types/Task/Task';

type Props = {
    task: Task;
};

export default function EngineeringReview({ task }: Props) {
    const getScreenFieldValue = useCallback(
        (dataName: string) => GetScreenFieldValue(task.screen.screenFields, dataName),
        [task.screen.screenFields]
    );

    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const [engineerPerformedBy, setEngineerPerformedBy] = useState(
        () => getScreenFieldValue(NCRDataConstants.ENGINEER_PERFORMED_BY)
    );
    const [subcontractorList, setSubcontractorList] = useState<string[]>([]);
    const [selectedSubcontractor, setSelectedSubcontractor] = useState(
        () => getScreenFieldValue(NCRDataConstants.ENGINEER_EXTERNAL_FIRM)
    );
    const [openMenu, setOpenMenu] = useState(false);
    const [subcontractorSearch, setSubcontractorSearch] = useState(false);
    const [subcontractorsLoaded, setSubcontractorsLoaded] = useState(false);

    const searchSubcontractors = useCallback(async (searchString: string) => {
        try {
            const targetDS = !!task.screen.screenFields.find(x => x.dataName === 'JDESource')?.value
                ? 'BOOMI_SUBCONTRACTORS_ENTERPRISE'
                : 'BOOMI_SUBCONTRACTORS';

            const data = (await DataService.searchDataSourceData(targetDS, '1', searchString)).data;
            if (!mountedRef.current) return;
            setSubcontractorList(data);
            setOpenMenu(true);
        } catch (e) {
            if (!mountedRef.current) return;
            console.log(e);
        }
    }, [task.screen.screenFields]);

    useEffect(() => {
        const loadSubcontractors = async () => {
            await searchSubcontractors('');
            setSubcontractorsLoaded(true);
        };
        loadSubcontractors();
    }, [searchSubcontractors]);

    const handleAssignedToSearch = (query: string) => {
        if (query.length > 0) {
            searchSubcontractors(query);
        }

        if (!subcontractorSearch) {
            setSelectedSubcontractor(query);
        }
    };

    const handleAssignToChange = (event: any) => {
        handleAssignedToSearch(event.target.value);
        setSelectedSubcontractor(event.target.value);
    };

    const changeMenu = (event: any) => {
        const value = event?.value ?? '';
        const search = !!event;

        setOpenMenu(false);
        setSelectedSubcontractor(value);
        setSubcontractorSearch(search);
    };

    const hideMenu = () => setOpenMenu(false);

    const handleEngineerPerformedByChange = (event: any) => {
        setEngineerPerformedBy(event.target.value);
    };

    const getOwnerValue = () => {
        const owner = getScreenFieldValue(NCRDataConstants.ENGINEER_OWNER);
        return owner || 'Owner';
    };

    return (
        <div>
            <Header title={NCRDataConstants.ENGINEER_HEADER} headerColor="white" textColor="black" />
            <TextArea
                dataName={NCRDataConstants.ENGINEER_COMMENTS}
                title={NCRDataConstants.ENGINEER_COMMENTS_TITLE}
                value={getScreenFieldValue(NCRDataConstants.ENGINEER_COMMENTS)}
                required={true}
            />
            <SelectField
                dataName={NCRDataConstants.ENGINEER_PERFORMED_BY}
                title={NCRDataConstants.ENGINEER_PERFORMED_BY_TITLE}
                value={engineerPerformedBy}
                onchange={handleEngineerPerformedByChange}
                required={true}
                options={[
                    { value: NCRDataConstants.ENGINEER_PERFORMED_BY_BLATTNER },
                    { value: NCRDataConstants.ENGINEER_PERFORMED_BY_EXTERNAL },
                    { value: getOwnerValue() },
                ]}
            />
            {engineerPerformedBy === NCRDataConstants.ENGINEER_PERFORMED_BY_EXTERNAL ? (
                !subcontractorsLoaded ? (
                    <div>
                        <Spinner animation="border" size="sm" />
                        <span> Loading...</span>
                        <input type="text" required={true} className="input-hidden" />
                    </div>
                ) : (
                    <div>
                        <b>
                            <label htmlFor={NCRDataConstants.ENGINEER_EXTERNAL_FIRM}>
                                {NCRDataConstants.ENGINEER_EXTERNAL_FIRM_TITLE}{' '}
                                <label className="required">*</label>
                            </label>
                        </b>
                        <br />
                        {selectedSubcontractor.length !== 0 ? (
                            <div>
                                <p className="HintText" />
                                <Select
                                    placeholder=""
                                    name={NCRDataConstants.ENGINEER_EXTERNAL_FIRM}
                                    defaultInputValue={selectedSubcontractor}
                                    autoFocus={true}
                                    onInputChange={handleAssignedToSearch}
                                    onChange={changeMenu}
                                    onBlur={hideMenu}
                                    options={subcontractorList.map((subcontractor) => ({
                                        value: subcontractor,
                                        label: subcontractor,
                                    }))}
                                    menuIsOpen={openMenu}
                                    components={{
                                        DropdownIndicator: () => null,
                                        IndicatorSeparator: () => null,
                                    }}
                                    isClearable={true}
                                />
                            </div>
                        ) : (
                            <TextField
                                dataName="AssignToPlaceholder"
                                title=""
                                value=""
                                readonly={false}
                                required={true}
                                placeholder="Search..."
                                onchange={handleAssignToChange}
                                autoFocus={true}
                            />
                        )}
                    </div>
                )
            ) : null}
        </div>
    );
}
